import PeerLink from "./peer-link.js";

// Fallback until the server sends its own list (which may include TURN)
export const ICE_SERVERS = [{ urls: "stun:stun.l.google.com:19302" }];

const MIC_CONSTRAINTS = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
};

const CAMERA_CONSTRAINTS = {
    width: { ideal: 320 },
    height: { ideal: 240 },
    frameRate: { ideal: 15, max: 15 },
};

const DEFAULT_POLICY = { micLocked: false, cameraLocked: false };

const SPEAKING_THRESHOLD = 0.02;
const SPEAKING_POLL_MS = 150;
const SPEAKING_HOLD_MS = 450;
const SELF = "self";

export default class VoiceCall {
    constructor(socket, { iceServers = ICE_SERVERS, isTeacher = false } = {}) {
        this.socket = socket;
        this.iceServers = iceServers;
        this.isTeacher = isTeacher;

        this.status = "idle"; // idle | joining | joined
        this.selfId = null;
        this.muted = true;
        this.camera = false;
        this.requestingMic = false;
        this.requestingCamera = false;
        this.selfSpeaking = false;
        this.audioBlocked = false;
        this.policy = DEFAULT_POLICY;

        this.micStream = null;
        this.cameraStream = null;
        this.participants = new Map(); // Map<socketID, Participant>
        this.links = new Map(); // Map<socketID, PeerLink>
        this.audioElements = new Map(); // Map<socketID, HTMLAudioElement>
        this.videoStreams = new Map(); // Map<socketID, MediaStream>
        this.meters = new Map(); // Map<socketID | "self", Meter>

        this.audioContext = null;
        this.meterTimer = null;
        this.listeners = new Set();

        this.socketHandlers = {
            "voice:participants": (data) => this._onParticipants(data),
            "voice:participant-joined": (data) =>
                this._onParticipantJoined(data),
            "voice:participant-left": (data) =>
                this._removeParticipant(data.id),
            "voice:state": (data) =>
                this._updateParticipant(data.id, {
                    muted: data.muted,
                    camera: !!data.camera,
                }),
            "voice:forced": (data) => this._onForced(data),
            "voice:policy": (data) => this._onPolicy(data),
            "voice:signal": (data) => this._onSignal(data),
            "voice:error": (data) => this._onServerError(data),
            disconnect: () => this._onSocketDisconnect(),
        };
        Object.entries(this.socketHandlers).forEach(([event, handler]) =>
            this.socket.on(event, handler),
        );
    }

    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    getState() {
        return {
            status: this.status,
            isTeacher: this.isTeacher,
            muted: this.muted,
            camera: this.camera,
            policy: { ...this.policy },
            micLocked: this.policy.micLocked && !this.isTeacher,
            cameraLocked: this.policy.cameraLocked && !this.isTeacher,
            requestingMic: this.requestingMic,
            requestingCamera: this.requestingCamera,
            selfSpeaking: this.selfSpeaking,
            audioBlocked: this.audioBlocked,
            participants: Array.from(this.participants.values(), (p) => ({
                ...p,
            })),
        };
    }

    join() {
        if (this.status !== "idle" || !this.socket.connected) return;

        this.status = "joining";
        this.muted = true;
        this._ensureAudioContext();
        this.socket.emit("voice:join");
        this._emitChange();
    }

    leave() {
        if (this.status === "idle") return;

        this.socket.emit("voice:leave");
        this._teardown();
    }

    async toggleMute() {
        if (this.muted) await this.unmute();
        else this.mute();
    }

    async unmute() {
        if (this.status !== "joined" || this.requestingMic || !this.muted)
            return;
        if (this.policy.micLocked && !this.isTeacher) {
            this._emit({ type: "error", reason: "mic-locked" });
            return;
        }

        if (!this.micStream) {
            this.requestingMic = true;
            this._emitChange();

            let stream = null;
            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    audio: MIC_CONSTRAINTS,
                });
            } catch (e) {
                console.warn("[Voice] Microphone not available", e);
            }
            this.requestingMic = false;

            if (!stream) {
                this._emit({ type: "error", reason: "mic-denied" });
                return;
            }

            // The user left the call while the permission prompt was open
            if (this.status !== "joined") {
                stream.getTracks().forEach((track) => track.stop());
                this._emitChange();
                return;
            }

            this.micStream = stream;
            this._addMeter(SELF, stream);
        }

        this._setMuted(false);
    }

    mute() {
        if (this.status !== "joined" || this.muted) return;
        this._setMuted(true);
    }

    async toggleCamera() {
        if (this.camera) this.disableCamera();
        else await this.enableCamera();
    }

    async enableCamera() {
        if (this.status !== "joined" || this.requestingCamera || this.camera)
            return;
        if (this.policy.cameraLocked && !this.isTeacher) {
            this._emit({ type: "error", reason: "camera-locked" });
            return;
        }

        this.requestingCamera = true;
        this._emitChange();

        let stream = null;
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                video: CAMERA_CONSTRAINTS,
            });
        } catch (e) {
            console.warn("[Voice] Camera not available", e);
        }
        this.requestingCamera = false;

        if (!stream) {
            this._emit({ type: "error", reason: "camera-denied" });
            this._emitChange();
            return;
        }

        // The call ended or the teacher locked cameras during the prompt
        const lockedMeanwhile = this.policy.cameraLocked && !this.isTeacher;
        if (this.status !== "joined" || lockedMeanwhile) {
            stream.getTracks().forEach((track) => track.stop());
            this._emitChange();
            return;
        }

        this.cameraStream = stream;
        this._setCamera(true);
    }

    disableCamera() {
        if (!this.camera) return;
        this._setCamera(false);
    }

    muteAll() {
        if (!this.isTeacher || this.status !== "joined") return;
        this.socket.emit("voice:mute-all");
    }

    setPolicy(changes) {
        if (!this.isTeacher || this.status !== "joined") return;
        this.socket.emit("voice:policy", changes);
    }

    getVideoStream(id) {
        return this.videoStreams.get(id) || null;
    }

    getLocalVideoStream() {
        return this.cameraStream;
    }

    resumeAudio() {
        this.audioBlocked = false;
        this.audioContext?.resume();
        this.audioElements.forEach((audio) => this._playAudio(audio));
        this._emitChange();
    }

    destroy() {
        this.leave();
        Object.entries(this.socketHandlers).forEach(([event, handler]) =>
            this.socket.off(event, handler),
        );
        this.listeners.clear();
    }

    _onParticipants({ selfId, participants, policy, iceServers }) {
        if (this.status === "idle") {
            this.socket.emit("voice:leave");
            return;
        }
        if (this.status !== "joining") return;

        this.status = "joined";
        this.selfId = selfId;
        this.policy = { ...DEFAULT_POLICY, ...policy };
        if (iceServers?.length) this.iceServers = iceServers;

        participants.forEach((participant) => {
            this._addParticipant(participant);
            this._createLink(participant.id, true);
        });

        this._startMeters();
        this._emitChange();
    }

    _onParticipantJoined(participant) {
        if (this.status !== "joined") return;

        this._addParticipant(participant);
        this._emitChange();
    }

    _onSignal({ from, description, candidate }) {
        if (this.status !== "joined" || !this.participants.has(from)) return;

        const link = this.links.get(from) || this._createLink(from, false);
        link.handleSignal({ description, candidate });
    }

    _onForced({ muted, camera, reason }) {
        if (this.status !== "joined") return;

        if (muted === true && !this.muted) this._setMuted(true, false);
        if (camera === false && this.camera) this._setCamera(false, false);

        if (reason) this._emit({ type: "notice", reason });
        this._emitChange();
    }

    _onPolicy(policy) {
        if (this.status !== "joined") return;

        const previous = this.policy;
        this.policy = { ...DEFAULT_POLICY, ...policy };

        if (!this.isTeacher) {
            if (previous.micLocked !== this.policy.micLocked)
                this._emit({
                    type: "notice",
                    reason: this.policy.micLocked
                        ? "mic-locked"
                        : "mic-unlocked",
                });
            if (previous.cameraLocked !== this.policy.cameraLocked)
                this._emit({
                    type: "notice",
                    reason: this.policy.cameraLocked
                        ? "camera-locked"
                        : "camera-unlocked",
                });
        }
        this._emitChange();
    }

    _onServerError({ reason }) {
        if (this.status === "joining") this._teardown();
        this._emit({ type: "error", reason });
    }

    _onSocketDisconnect() {
        if (this.status === "idle") return;

        this._teardown();
        this._emit({ type: "error", reason: "disconnected" });
    }

    _addParticipant(participant) {
        this.participants.set(participant.id, {
            id: participant.id,
            username: participant.username,
            color: participant.color,
            avatar: participant.avatar ?? null,
            isTeacher: !!participant.isTeacher,
            muted: participant.muted !== false,
            camera: !!participant.camera,
            speaking: false,
            connection: "connecting",
        });
    }

    _updateParticipant(id, changes) {
        const participant = this.participants.get(id);
        if (!participant) return;

        Object.assign(participant, changes);
        this._emitChange();
    }

    _removeParticipant(id) {
        this.links.get(id)?.close();
        this.links.delete(id);
        this._removeAudio(id);
        this.videoStreams.delete(id);
        this._removeMeter(id);

        if (this.participants.delete(id)) this._emitChange();
    }

    _createLink(id, initiator) {
        const link = new PeerLink({
            remoteId: id,
            polite: this.selfId < id,
            initiator,
            iceServers: this.iceServers,
            sendSignal: (signal) =>
                this.socket.emit("voice:signal", { to: id, ...signal }),
            onTrack: (track) => this._attachRemoteTrack(id, track),
            onStateChange: (state) =>
                this._updateParticipant(id, { connection: state }),
        });

        link.setLocalTrack("audio", this._currentAudioTrack());
        link.setLocalTrack("video", this._currentVideoTrack());
        this.links.set(id, link);
        return link;
    }

    _setMuted(muted, notifyServer = true) {
        this.muted = muted;
        this.micStream?.getAudioTracks().forEach((track) => {
            track.enabled = !muted;
        });

        const track = this._currentAudioTrack();
        this.links.forEach((link) => link.setLocalTrack("audio", track));

        if (notifyServer) this.socket.emit("voice:state", { muted });
        this._emitChange();
    }

    _setCamera(camera, notifyServer = true) {
        this.camera = camera;

        if (!camera) {
            this.cameraStream?.getTracks().forEach((track) => track.stop());
            this.cameraStream = null;
        }

        const track = this._currentVideoTrack();
        this.links.forEach((link) => link.setLocalTrack("video", track));

        if (notifyServer) this.socket.emit("voice:state", { camera });
        this._emitChange();
    }

    _currentAudioTrack() {
        if (this.muted || !this.micStream) return null;
        return this.micStream.getAudioTracks()[0] || null;
    }

    _currentVideoTrack() {
        if (!this.camera || !this.cameraStream) return null;
        return this.cameraStream.getVideoTracks()[0] || null;
    }

    _teardown() {
        this.links.forEach((link) => link.close());
        this.links.clear();
        Array.from(this.audioElements.keys()).forEach((id) =>
            this._removeAudio(id),
        );
        Array.from(this.meters.keys()).forEach((id) => this._removeMeter(id));
        this._stopMeters();

        this.micStream?.getTracks().forEach((track) => track.stop());
        this.micStream = null;
        this.cameraStream?.getTracks().forEach((track) => track.stop());
        this.cameraStream = null;
        this.videoStreams.clear();
        this.audioContext?.close();
        this.audioContext = null;

        this.participants.clear();
        this.status = "idle";
        this.selfId = null;
        this.muted = true;
        this.camera = false;
        this.requestingMic = false;
        this.requestingCamera = false;
        this.selfSpeaking = false;
        this.audioBlocked = false;
        this.policy = DEFAULT_POLICY;

        this._emitChange();
    }

    _attachRemoteTrack(id, track) {
        const stream = new MediaStream([track]);

        if (track.kind === "video") {
            this.videoStreams.set(id, stream);
            this._emitChange();
            return;
        }

        let audio = this.audioElements.get(id);
        if (!audio) {
            audio = document.createElement("audio");
            audio.autoplay = true;
            audio.dataset.voicePeer = id;
            this._getAudioContainer().appendChild(audio);
            this.audioElements.set(id, audio);
        }

        if (audio.srcObject !== stream) audio.srcObject = stream;
        this._playAudio(audio);
        this._addMeter(id, stream);
    }

    _playAudio(audio) {
        audio.play().catch((e) => {
            if (e.name !== "NotAllowedError") return;
            this.audioBlocked = true;
            this._emitChange();
        });
    }

    _removeAudio(id) {
        const audio = this.audioElements.get(id);
        if (!audio) return;

        audio.srcObject = null;
        audio.remove();
        this.audioElements.delete(id);
    }

    _getAudioContainer() {
        let container = document.getElementById("ls-voice-audio");
        if (!container) {
            container = document.createElement("div");
            container.id = "ls-voice-audio";
            container.hidden = true;
            document.body.appendChild(container);
        }
        return container;
    }

    _ensureAudioContext() {
        if (this.audioContext) return;

        const AudioContextClass =
            window.AudioContext || window.webkitAudioContext;
        if (AudioContextClass) this.audioContext = new AudioContextClass();
    }

    _addMeter(id, stream) {
        if (!this.audioContext) return;
        this._removeMeter(id);

        const source = this.audioContext.createMediaStreamSource(stream);
        const analyser = this.audioContext.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);

        this.meters.set(id, {
            source,
            analyser,
            data: new Float32Array(analyser.fftSize),
            lastLoud: 0,
        });
    }

    _removeMeter(id) {
        const meter = this.meters.get(id);
        if (!meter) return;

        meter.source.disconnect();
        this.meters.delete(id);
    }

    _startMeters() {
        if (this.meterTimer) return;
        this.meterTimer = setInterval(
            () => this._pollMeters(),
            SPEAKING_POLL_MS,
        );
    }

    _stopMeters() {
        clearInterval(this.meterTimer);
        this.meterTimer = null;
    }

    _pollMeters() {
        const now = performance.now();
        let changed = false;

        this.meters.forEach((meter, id) => {
            meter.analyser.getFloatTimeDomainData(meter.data);

            let sum = 0;
            for (const sample of meter.data) sum += sample * sample;
            if (Math.sqrt(sum / meter.data.length) > SPEAKING_THRESHOLD)
                meter.lastLoud = now;

            const recentlyLoud = now - meter.lastLoud < SPEAKING_HOLD_MS;

            if (id === SELF) {
                const speaking = recentlyLoud && !this.muted;
                if (speaking !== this.selfSpeaking) {
                    this.selfSpeaking = speaking;
                    changed = true;
                }
                return;
            }

            const participant = this.participants.get(id);
            if (!participant) return;

            const speaking = recentlyLoud && !participant.muted;
            if (speaking !== participant.speaking) {
                participant.speaking = speaking;
                changed = true;
            }
        });

        if (changed) this._emitChange();
    }

    _emit(event) {
        this.listeners.forEach((listener) => {
            try {
                listener(event);
            } catch (e) {
                console.error("[Voice] Listener error", e);
            }
        });
    }

    _emitChange() {
        this._emit({ type: "change" });
    }
}
